import { type FormEvent, useEffect, useRef, useState } from "react";

import { type PantryItemView, api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { pantryCopy } from "../lib/i18n/pantry";

type Props = {
  householdId: string;
  item: PantryItemView;
  onSaved: (updated: PantryItemView) => void;
  onCancel: () => void;
};

const UNITS = ["g", "kg", "ml", "l", "szt", "opak", "łyż", "szkl"] as const;
const CATEGORIES = [
  "nabiał",
  "mięso",
  "warzywa",
  "owoce",
  "pieczywo",
  "suche",
  "napoje",
  "mrożone",
  "słodycze",
  "przyprawy",
  "inne",
] as const;

type Unit = (typeof UNITS)[number];
type Category = (typeof CATEGORIES)[number];

function toDateInputValue(timestamp: number | null): string {
  if (!timestamp) return "";
  const d = new Date(timestamp);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function PantryEditModal({ householdId, item, onSaved, onCancel }: Props) {
  const { lang } = useLang();
  const t = pantryCopy[lang];
  const ref = useRef<HTMLDialogElement | null>(null);
  const [name, setName] = useState(item.name);
  const [quantity, setQuantity] = useState(item.quantity !== null ? String(item.quantity) : "");
  const [unit, setUnit] = useState<Unit>((item.unit as Unit | null) ?? "szt");
  const [category, setCategory] = useState<Category>((item.category as Category | null) ?? "inne");
  const [isStaple, setIsStaple] = useState(item.isStaple);
  const [expires, setExpires] = useState(toDateInputValue(item.expiresAt));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (!node.open) node.showModal();
    return () => {
      if (node.open) node.close();
    };
  }, []);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const onCancelNative = (e: Event) => {
      e.preventDefault();
      onCancel();
    };
    node.addEventListener("cancel", onCancelNative);
    return () => node.removeEventListener("cancel", onCancelNative);
  }, [onCancel]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const expiresAt = expires ? Date.parse(`${expires}T00:00:00.000Z`) : null;
      const updated = await api.updatePantryItem(householdId, item.id, {
        name: name.trim() || item.name,
        quantity: quantity === "" ? null : Number(quantity),
        unit,
        category,
        isStaple,
        expiresAt,
      });
      onSaved(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.errSave);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <dialog
      ref={ref}
      aria-labelledby="edit-pantry-title"
      // max-h-[90dvh] + overflow-y-auto: when iOS keyboard opens for the
      // name / quantity inputs the dialog stays scrollable rather than
      // pushing fields off-screen (handoff §3.3). dvh tracks the visible
      // viewport so the cap shrinks with the keyboard.
      className="bg-[var(--surface)] rounded-xl p-5 w-full max-w-md max-h-[90dvh] overflow-y-auto backdrop:bg-black/70"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <h2
          id="edit-pantry-title"
          className="text-[length:var(--text-h3)] font-bold tracking-[-0.02em] text-[var(--ink)]"
        >
          {t.editTitle}
        </h2>

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm text-[var(--ink-2)]">{t.editFieldName}</span>
          <input
            type="text"
            required
            maxLength={200}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-11 px-3 bg-[var(--surface-2)] rounded-md text-[16px] shadow-[inset_0_0_0_1px_var(--nav-border)] focus:shadow-[inset_0_0_0_2px_var(--accent)] outline-none"
          />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-body-sm text-[var(--ink-2)]">{t.editFieldQuantity}</span>
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step={0.1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="h-11 px-3 bg-[var(--surface-2)] rounded-md text-[16px] shadow-[inset_0_0_0_1px_var(--nav-border)] focus:shadow-[inset_0_0_0_2px_var(--accent)] outline-none"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-body-sm text-[var(--ink-2)]">{t.editFieldUnit}</span>
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value as Unit)}
              className="h-11 px-3 bg-[var(--surface-2)] rounded-md text-[16px] shadow-[inset_0_0_0_1px_var(--nav-border)] focus:shadow-[inset_0_0_0_2px_var(--accent)] outline-none"
            >
              {UNITS.map((u) => (
                <option key={u} value={u}>
                  {t.unitLabel[u]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm text-[var(--ink-2)]">{t.editFieldCategory}</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as Category)}
            className="h-11 px-3 bg-[var(--surface-2)] rounded-md text-[16px] shadow-[inset_0_0_0_1px_var(--nav-border)] focus:shadow-[inset_0_0_0_2px_var(--accent)] outline-none"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t.categoryLabel[c]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm text-[var(--ink-2)]">{t.editFieldExpiry}</span>
          <input
            type="date"
            value={expires}
            onChange={(e) => setExpires(e.target.value)}
            className="h-11 px-3 bg-[var(--surface-2)] rounded-md text-[16px] shadow-[inset_0_0_0_1px_var(--nav-border)] focus:shadow-[inset_0_0_0_2px_var(--accent)] outline-none"
          />
        </label>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isStaple}
            onChange={(e) => setIsStaple(e.target.checked)}
            className="w-5 h-5"
          />
          <span className="text-body-sm text-[var(--ink-2)]">{t.editStaple}</span>
        </label>

        {error ? <p className="text-caption text-[var(--color-error)]">{error}</p> : null}

        <div className="flex flex-col gap-2 pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="h-11 px-4 bg-[var(--accent)] text-[var(--accent-ink)] rounded-md font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? t.saving : t.save}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="h-11 px-4 text-[var(--ink-2)] rounded-md transition-colors hover:bg-[var(--surface-2)]"
          >
            {t.cancel}
          </button>
        </div>
      </form>
    </dialog>
  );
}
